"use client";

import { JSX, useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, EffectFade, Autoplay } from "swiper/modules";
import Link from "next/link";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-fade";

import Image from "next/image";
import { motion } from "framer-motion";
import Button from "@/Components/Button";
import ThumbSlide from "./ThumbSlide";
import { toWebp } from "@/utils/common";
import { SlideItem } from ".";

const baseUrl = `${process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL}`;

interface CustomSliderProps {
  SlideItem?: SlideItem[];
}

const CustomSliderWithSwiper: React.FC<CustomSliderProps> = ({ SlideItem = [],
}) => {
  const mainSwiperRef = useRef<any>(null);
  const thumbSwiperRef = useRef<any>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const totalSlides = SlideItem.length;

  const [direction, setDirection] = useState<"horizontal" | "vertical">(
    "horizontal",
  );

  useEffect(() => {
    const handleResize = () => {
      setDirection(window.innerWidth < 1024 ? "vertical" : "horizontal");
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle main slider change and sync with thumbnail slider
  const handleSlideChange = (swiper: any) => {
    const newIndex = swiper.realIndex;
    setCurrentIndex(newIndex);

    // Sync thumbnail slider to show current slide
    if (thumbSwiperRef.current) {
      // Calculate which slide should be visible in thumbnail slider
      // For vertical: show current slide at top, for horizontal: center it
      if (direction === "vertical") {
        thumbSwiperRef.current.slideTo(newIndex);
      } else {
        // For horizontal, we want to center the active slide if possible
        const slidesToShow = 2; // Based on your slidesPerView
        const targetSlide = Math.max(
          0,
          newIndex - Math.floor(slidesToShow / 2),
        );
        thumbSwiperRef.current.slideTo(targetSlide);
      }
    }
  };

  // Handle thumbnail click
  const handleThumbClick = (index: number) => {
    if (mainSwiperRef.current) {
      mainSwiperRef.current.slideToLoop(index);
    }
  };

  return (
    <div className="relative w-full">
      {/* Main Slider */}
      <Swiper
        modules={[EffectFade, Navigation, Autoplay]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: false,
        }}
        loop={true}
        navigation={{
          nextEl: ".swiper-button-next-custom",
          prevEl: ".swiper-button-prev-custom",
        }}
        onSlideChange={handleSlideChange}
        onSwiper={(swiper) => (mainSwiperRef.current = swiper)}
        style={{ willChange: 'transform' }}
        className="relative w-full xl:h-[607px] lg:h-[508px] h-[520px] min-h-[450px]"
      >
        {SlideItem.map((slide) => (
          <SwiperSlide key={slide.id}>
            <div className="relative w-full h-full after:bg-[linear-gradient(180deg,_rgba(0,0,0,0.08)_0%,_rgba(0,0,0,0.8)_60.02%)] after:content-[''] after:absolute after:w-full after:h-full after:top-0 after:right-0">
              <Image
                src={`${baseUrl}${toWebp(slide?.image?.data?.attributes?.url)}`}
                fill
                quality={80}
                style={{ aspectRatio: '16 / 9' }}
                className="object-cover"
                alt={slide?.title}
                loading="lazy"
                sizes="(max-width: 1024px) 100vw, (max-width: 1280px) 80vw, 50vw"
              />
              <div className="absolute z-10 xl:bottom-10 lg:bottom-8 bottom-0 xl:left-10 lg:left-8 left-0 xl:max-w-[540px] max-w-[557px] lg:w-[50%] lg:p-0 p-6 w-full">
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-subtitle-3 font-regular text-secondary-text-white border border-border-white w-fit p-2.5 pb-1.5"
                  key={"tag" + currentIndex}
                >
                  {slide.tag}
                </motion.p>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="text-heading-3 font-bold text-primary-text-white mt-5"
                  key={"title" + currentIndex}
                >
                  {slide.title}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="text-subtitle-2 font-regular text-secondary-text-white mt-2"
                  key={currentIndex}
                >
                  {slide.description}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                  className="mt-4"
                  key={"btn" + currentIndex}
                >
                  {slide?.link?.label && slide?.link?.href && (
                    <Link
                      href={slide?.link?.href}
                    >
                      <Button
                        label={slide?.link?.label}
                        onClick={slide.onButtonClick}
                      />
                    </Link>
                  )}
                </motion.div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Thumb Slider */}
      <div className="lg:absolute bottom-0 right-0 lg:w-[45%] w-full lg:max-w-[436px] overflow-hidden xl:pb-10 lg:pb-8 xl:pr-10 lg:pr-8 lg:mt-0 mt-5">
        <div className="relative z-20 items-center justify-end gap-3 mb-8 lg:flex hidden">
          {/* Prev Button */}
          <button
            aria-label="arrow"
            className="p-2 border swiper-button-prev-custom border-border-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed opacity-70 hover:opacity-100 transition-all duration-300"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M9.57 5.93L3.5 12l6.07 6.07"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20.5 12H3.67"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Counter */}
          <div className="text-subtitle-1 font-bold text-secondary-text-white">
            {currentIndex + 1} / {totalSlides}
          </div>

          {/* Next Button */}
          <button
            className="p-2 border border-border-white swiper-button-next-custom cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed opacity-70 hover:opacity-100 transition-all duration-300"
            aria-label="Next slide"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M14.43 5.93L20.5 12l-6.07 6.07"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3.5 12h16.83"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <Swiper
          spaceBetween={20}
          slidesPerView={2}
          watchSlidesProgress
          slideToClickedSlide={false}
          direction={direction}
          onSwiper={(swiper) => (thumbSwiperRef.current = swiper)}
          speed={300}
          modules={[Autoplay]}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
          }}
          loop={true}
          className="max-h-[277px]"
        >
          {SlideItem.reduce((acc, slide, actualIndex) => {
            if (actualIndex === currentIndex) return acc;

            const visibleIndex = acc.length;

            acc.push(
              <SwiperSlide
                key={slide.id}
                className="lg:max-w-[188px] max-w-full lg:max-h-full max-h-[115px]"
              >
                <div
                  className={`cursor-pointer transition-all duration-300 ${actualIndex === currentIndex
                      ? "opacity-100 scale-105"
                      : "opacity-70 hover:opacity-90"
                    }`}
                  onClick={() => handleThumbClick(actualIndex)}
                >
                  <ThumbSlide
                    bgColor={visibleIndex % 2 === 0 ? "blue" : "green"}
                    title={slide.tag}
                    description={slide.description}
                    isActive={actualIndex === currentIndex}
                  />
                </div>
              </SwiperSlide>,
            );
            return acc;
          }, [] as JSX.Element[])}
        </Swiper>
      </div>
    </div>
  );
};
export default CustomSliderWithSwiper;